import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Undo2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

const FALLBACK = {
  introEyebrow: 'A WEDDING CELEBRATION',
  introDateShort: '07 · 31 · 2027',
  coupleFirst: 'Mary',
  coupleSecond: 'Arrington',
  hashtag: '#GodBlessTheJohnsons',
  motto: 'Grace Made Us. Purpose Unites Us.',
  scripture: {
    text: '"...At the right time, I, the Lord, will make it happen."',
    reference: '— Isaiah 60:22',
  },
  dateDisplay: 'Saturday, July 31, 2027',
  weddingDateISO: '2027-07-31T15:00:00',
  ctaLabel: 'View Details',
};

// Convert a full ISO datetime (with seconds) to the `<input type="datetime-local">`
// value format (YYYY-MM-DDTHH:mm) and back. Kept lossless for the seconds field.
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return m ? `${m[1]}T${m[2]}` : iso;
};

/**
 * AdminHeroManager
 *
 * CMS-backed editor for the home-page hero. The public hero polls the same
 * singleton every 15 s + on visibility-change so a Save Changes shows up on
 * every open tab within seconds — no redeploy needed.
 */
const AdminHeroManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Ticks every second so the live "Countdown preview" number updates
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/hero/content`);
      const data = { ...FALLBACK, ...(res.data || {}) };
      setInitial(clone(data));
      setContent(clone(data));
    } catch { setError('Could not load hero content.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (fn) => setContent((prev) => { const n = clone(prev); fn(n); return n; });
  const dirty = useMemo(
    () => content && initial && JSON.stringify(content) !== JSON.stringify(initial),
    [content, initial]
  );

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError('');
    try {
      const payload = { ...content };
      delete payload.id; delete payload.updated_at;
      const res = await axios.put(`${API}/admin/hero/content`, payload, { headers: authHeader() });
      const data = { ...FALLBACK, ...(res.data || {}) };
      setInitial(clone(data));
      setContent(clone(data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save.');
    }
    finally { setSaving(false); }
  };

  const revert = () => setContent(clone(initial));

  const resetDefaults = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/admin/hero/content/reset`, {}, { headers: authHeader() });
      await load();
      setResetOpen(false);
    } catch { setError('Failed to reset content.'); }
    finally { setResetting(false); }
  };

  if (loading || !content) {
    return (
      <div className="bg-white rounded-2xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2" data-testid="admin-hero-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Hero…
      </div>
    );
  }

  const scripture = content.scripture || {};

  // Live countdown preview using the current CMS ISO — proves the countdown
  // will re-target the moment Save Changes is clicked.
  const weddingTs = Date.parse(content.weddingDateISO || FALLBACK.weddingDateISO);
  const diff = Number.isNaN(weddingTs) ? 0 : Math.max(0, weddingTs - nowTs);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);

  return (
    <div className="space-y-4" data-testid="admin-hero">
      {/* Sticky action bar */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Sparkles className="w-6 h-6 text-[#C8A951] mt-1" />
          <div>
            <h2 className="font-serif text-2xl text-[#3B2F2F]">Home Hero</h2>
            <p className="text-xs text-[#8A7A7A] max-w-xl">
              Every field on the home-page hero is editable here. Save Changes writes to the database —
              no redeploy needed. The countdown automatically retargets to the wedding date you set below.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)} className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1" data-testid="hero-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={revert} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="hero-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60" data-testid="hero-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="grid md:grid-cols-[1.05fr_1fr] gap-4">
        {/* Editable fields */}
        <div className="space-y-4">
          {/* Names */}
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6 space-y-4">
            <h3 className="font-serif text-xl text-[#3B2F2F]">Couple names</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">First name</label>
                <Input value={content.coupleFirst || ''} onChange={(e) => patch((c) => { c.coupleFirst = e.target.value; })} data-testid="hero-couple-first" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Second name</label>
                <Input value={content.coupleSecond || ''} onChange={(e) => patch((c) => { c.coupleSecond = e.target.value; })} data-testid="hero-couple-second" />
              </div>
            </div>
          </div>

          {/* Hashtag & motto */}
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6 space-y-4">
            <h3 className="font-serif text-xl text-[#3B2F2F]">Tagline</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Hashtag (top eyebrow)</label>
              <Input value={content.hashtag || ''} onChange={(e) => patch((c) => { c.hashtag = e.target.value; })} data-testid="hero-hashtag-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Motto / tagline</label>
              <Input value={content.motto || ''} onChange={(e) => patch((c) => { c.motto = e.target.value; })} data-testid="hero-motto-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Scripture / quote</label>
              <Textarea rows={2} value={scripture.text || ''} onChange={(e) => patch((c) => { c.scripture = { ...(c.scripture || {}), text: e.target.value }; })} data-testid="hero-scripture-text" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Scripture reference</label>
              <Input value={scripture.reference || ''} onChange={(e) => patch((c) => { c.scripture = { ...(c.scripture || {}), reference: e.target.value }; })} placeholder="— Isaiah 60:22" data-testid="hero-scripture-ref" />
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6 space-y-4">
            <h3 className="font-serif text-xl text-[#3B2F2F]">Wedding date</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Date display (pill)</label>
              <Input value={content.dateDisplay || ''} onChange={(e) => patch((c) => { c.dateDisplay = e.target.value; })} placeholder="Saturday, July 31, 2027" data-testid="hero-date-display" />
              <p className="text-[11px] text-[#8A7A7A] mt-1">Shown as-is in the small pill above the countdown.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Countdown target (used by the timer)</label>
              <Input
                type="datetime-local"
                value={isoToLocalInput(content.weddingDateISO)}
                onChange={(e) => {
                  const v = e.target.value;                     // "YYYY-MM-DDTHH:mm"
                  const withSeconds = v ? `${v}:00` : '';
                  patch((c) => { c.weddingDateISO = withSeconds; });
                }}
                data-testid="hero-wedding-datetime"
              />
              <p className="text-[11px] text-[#8A7A7A] mt-1">
                Changing this instantly retargets the countdown on the public hero — no redeploy needed.
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Intro date (short form)</label>
              <Input value={content.introDateShort || ''} onChange={(e) => patch((c) => { c.introDateShort = e.target.value; })} placeholder="07 · 31 · 2027" data-testid="hero-intro-date-short" />
              <p className="text-[11px] text-[#8A7A7A] mt-1">Shown once during the cinematic intro overlay on first visit.</p>
            </div>
          </div>

          {/* Intro & CTA */}
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6 space-y-4">
            <h3 className="font-serif text-xl text-[#3B2F2F]">Intro &amp; CTA</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Intro eyebrow</label>
              <Input value={content.introEyebrow || ''} onChange={(e) => patch((c) => { c.introEyebrow = e.target.value; })} placeholder="A WEDDING CELEBRATION" data-testid="hero-intro-eyebrow" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">CTA button label</label>
              <Input value={content.ctaLabel || ''} onChange={(e) => patch((c) => { c.ctaLabel = e.target.value; })} placeholder="View Details" data-testid="hero-cta-label" />
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-[#3B2F2F] rounded-2xl shadow-sm p-6 md:p-8 sticky top-24 self-start" data-testid="hero-live-preview">
          <p className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-4">Live preview</p>
          <div className="flex items-center gap-3 mb-4 text-white/80">
            <span className="flex-1 h-px bg-[#C8A951]/40" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#C8A951]" data-testid="hero-preview-hashtag">{content.hashtag}</span>
            <span className="flex-1 h-px bg-[#C8A951]/40" />
          </div>
          <h1 className="font-serif text-white text-4xl leading-tight text-center" data-testid="hero-preview-first">{content.coupleFirst}</h1>
          <p className="font-cursive text-[#C8A951] text-4xl text-center leading-none my-2">&amp;</p>
          <h1 className="font-serif text-white text-4xl leading-tight text-center" data-testid="hero-preview-second">{content.coupleSecond}</h1>
          <p className="text-center text-white/75 text-xs uppercase tracking-[0.28em] mt-4" data-testid="hero-preview-motto">{content.motto}</p>
          <p className="text-center text-white/60 italic text-sm mt-3">
            {scripture.text}
            <span className="block text-[#C8A951] text-xs mt-1 not-italic">{scripture.reference}</span>
          </p>
          <p className="text-center text-white/85 text-xs uppercase tracking-[0.28em] mt-5" data-testid="hero-preview-date">— {content.dateDisplay} —</p>

          <div className="grid grid-cols-4 gap-2 mt-5 bg-white/5 rounded-xl border border-white/15 py-3" data-testid="hero-preview-countdown">
            {[
              { v: d, l: 'Days' },
              { v: h, l: 'Hours' },
              { v: m, l: 'Min' },
              { v: s, l: 'Sec' },
            ].map((it) => (
              <div key={it.l} className="text-center">
                <div className="font-serif text-white text-2xl leading-none">{String(it.v).padStart(2, '0')}</div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest mt-1">{it.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/40 text-white text-[11px] uppercase tracking-[0.28em]" data-testid="hero-preview-cta">
              {content.ctaLabel}
            </span>
          </div>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Home Hero to defaults?</DialogTitle>
            <DialogDescription>
              This restores the couple names, hashtag, motto, scripture, dates, intro eyebrow, and CTA to their originals.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetDefaults} disabled={resetting} data-testid="hero-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHeroManager;
